/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 */
import React, { useId } from 'react';

const COVER_STYLE = { objectFit: 'cover' };

/**
 * Brand mark only (no wordmark) — MARCHIO UFFICIALE SANITÀNEXUS (design originale):
 *  - sfondo verde con gradiente primary → primaryAlt e riflesso radiale
 *  - croce medica bianca arrotondata (salute)
 *  - cluster di 3 nodi di rete collegati in alto a destra, colore accent (Nexus = rete)
 *  - tracciato del battito in basso (parametri vitali)
 *
 * Lo stesso disegno è replicato in `public/icon.svg`, nelle PNG PWA (scripts/gen_brand_icons.py)
 * e in `lib/brandIcons.js` (favicon dinamica): UNA sola variante ovunque.
 * Se la clinica ha caricato un logo (Admin → Brand) viene mostrato quello.
 * Gli id dei gradienti sono univoci per istanza (useId): più loghi nella stessa pagina non si
 * "rubano" il gradiente a vicenda.
 */
export const LogoMark = ({ size, name, logoUrl, primary, primaryAlt, accent }) => {
  const uid = useId().replace(/:/g, '');
  if (logoUrl) {
    return <img src={logoUrl} alt={name} width={size} height={size} className="rounded-2xl drop-shadow-sm" style={COVER_STYLE} />;
  }
  const gradId = `snx-grad-${uid}`;
  const shineId = `snx-shine-${uid}`;
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" role="img" aria-label={name} className="drop-shadow-sm" data-testid="brand-logo-mark">
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={primary} />
          <stop offset="1" stopColor={primaryAlt || primary} />
        </linearGradient>
        <radialGradient id={shineId} cx="30%" cy="28%" r="70%">
          <stop offset="0" stopColor="rgba(255,255,255,0.35)" />
          <stop offset="1" stopColor="rgba(255,255,255,0)" />
        </radialGradient>
      </defs>
      <rect x="2" y="2" width="60" height="60" rx="16" fill={`url(#${gradId})`} />
      <rect x="2" y="2" width="60" height="60" rx="16" fill={`url(#${shineId})`} />
      {/* Croce medica */}
      <g fill="#ffffff" fillOpacity="0.97">
        <rect x="27.5" y="14" width="9" height="36" rx="3.5" />
        <rect x="14" y="27.5" width="36" height="9" rx="3.5" />
      </g>
      {/* Nodi di rete (Nexus) */}
      <g fill={accent}>
        <circle cx="49" cy="14" r="2.4" />
        <circle cx="54.5" cy="20.5" r="1.7" />
        <circle cx="49" cy="27" r="1.7" />
      </g>
      <g stroke={accent} strokeWidth="1.1" strokeLinecap="round" opacity="0.85">
        <line x1="49" y1="14" x2="54.5" y2="20.5" />
        <line x1="54.5" y1="20.5" x2="49" y2="27" />
        <line x1="49" y1="14" x2="49" y2="27" />
      </g>
      {/* Battito */}
      <path d="M8 55 L18 55 L22 50 L26 60 L32 46 L38 58 L44 55 L56 55" fill="none" stroke="#ECFDF5" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" opacity="0.65" />
    </svg>
  );
};
